import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VideocamOffIcon from "@mui/icons-material/VideocamOffOutlined";
import { useCallback, useEffect, useState } from "react";
import {
  emptyCareProfileInput,
  type CareProfileInput,
  type CareTeamMember,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { EditDrawer, type Dismiss } from "./EditDrawer";
import { PermissionGate } from "../auth/access";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { errorText } from "../utils/apiError";

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident whose profile we're editing (PhiRecord id). */
  personRef: string;
  /** Human label for the room, for the dialog title. */
  roomLabel: string;
  onClose: () => void;
  /** Fired after a successful save so the parent can surface a notice. */
  onSaved?: () => void;
}

/**
 * Staff authoring for a resident's in-room clinical whiteboard: allergies,
 * isolation, fall risk, diet, precautions, care team and a free-text goal.
 * The profile is keyed to the resident (personRef) and carries no identifiers,
 * so this is gated on `canManageCareProfiles` (same as the roster) - not PHI
 * reveal. Loads the current profile on open; an unauthored resident starts blank.
 */
export default function CareProfileDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const [draft, setDraft] = useState<CareProfileInput>(emptyCareProfileInput());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await foyerApi.getCareProfile(premiseId, personRef);
      setDraft({
        allergies: p.allergies,
        isolation: p.isolation,
        fallRisk: p.fallRisk,
        diet: p.diet,
        precautions: p.precautions,
        careTeam: p.careTeam,
        notes: p.notes,
        // The resident-level video-call opt-out (§4.8). Round-tripped so the
        // toggle reflects the stored value and a Save can't silently re-enable
        // calls by omitting it.
        videoCallsDisabled: p.videoCallsDisabled,
      });
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoading(false);
    }
  }, [premiseId, personRef, t]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const patch = (p: Partial<CareProfileInput>) =>
    setDraft((d) => ({ ...d, ...p }));

  const setTeamMember = (i: number, m: Partial<CareTeamMember>) =>
    patch({
      careTeam: draft.careTeam.map((member, j) =>
        i === j ? { ...member, ...m } : member
      ),
    });
  const addTeamMember = () =>
    patch({ careTeam: [...draft.careTeam, { role: "", name: "" }] });
  const removeTeamMember = (i: number) =>
    patch({ careTeam: draft.careTeam.filter((_, j) => j !== i) });

  const save = async (dismiss: Dismiss) => {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.saveCareProfile(premiseId, personRef, draft);
      toast.success(t("careProfileDialog.saved", { room: roomLabel }));
      // Slide out first, then let the page react and unmount us.
      dismiss(() => {
        onSaved?.();
        onClose();
      });
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(t, "saveCareProfile", err, { room: roomLabel })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    // Edits an existing resident, so it slides in from the right.
    <EditDrawer
      open={open}
      onClose={onClose}
      busy={saving}
      title={t("careProfileDialog.title", { room: roomLabel })}
      data-testid="care-profile-dialog"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="residents"
            level="edit"
            place={{ premiseId }}
          >
            <Button
              variant="contained"
              onClick={() => void save(dismiss)}
              disabled={saving || loading}
              data-testid="profile-save"
            >
              {saving
                ? t("common:states.saving")
                : t("careProfileDialog.saveProfile")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      {loading ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Resident-level video-call opt-out (design §4.8). Deliberately the
                FIRST control in the dialog and given its own bordered, tinted
                box - it is a privacy decision that overrides every other setting
                (premise policy, contact flags, staff), so it must never read as
                one clinical toggle among many. */}
          <Box
            sx={{
              border: 1,
              borderColor: draft.videoCallsDisabled ? "error.main" : "divider",
              bgcolor: draft.videoCallsDisabled
                ? "error.light"
                : "action.hover",
              borderRadius: 1,
              p: 2,
            }}
          >
            <PermissionGate
              resource="video-calls"
              level="edit"
              place={{ premiseId }}
              deniedProps={{ disabled: true }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={draft.videoCallsDisabled ?? false}
                    onChange={(e) =>
                      patch({ videoCallsDisabled: e.target.checked })
                    }
                    color="error"
                    data-testid="profile-video-calls-disabled"
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <VideocamOffIcon fontSize="small" />
                      {t("careProfileDialog.videoCallsOff")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("careProfileDialog.videoCallsOffHelp")}
                    </Typography>
                  </Box>
                }
              />
            </PermissionGate>
          </Box>

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={draft.allergies}
            onChange={(_, v) => patch({ allergies: v as string[] })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("careProfileDialog.allergies")}
                placeholder={t("careProfileDialog.typeAndEnter")}
                inputProps={{
                  ...params.inputProps,
                  "data-testid": "profile-allergies",
                }}
              />
            )}
          />

          <TextField
            size="small"
            label={t("careProfileDialog.isolation")}
            placeholder={t("careProfileDialog.isolationPlaceholder")}
            value={draft.isolation ?? ""}
            onChange={(e) => patch({ isolation: e.target.value || null })}
            fullWidth
            inputProps={{ "data-testid": "profile-isolation" }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={draft.fallRisk}
                onChange={(e) => patch({ fallRisk: e.target.checked })}
                data-testid="profile-fallrisk"
              />
            }
            label={t("careProfileDialog.fallRisk")}
          />

          <TextField
            size="small"
            label={t("careProfileDialog.diet")}
            placeholder={t("careProfileDialog.dietPlaceholder")}
            value={draft.diet ?? ""}
            onChange={(e) => patch({ diet: e.target.value || null })}
            fullWidth
            inputProps={{ "data-testid": "profile-diet" }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={draft.precautions}
            onChange={(_, v) => patch({ precautions: v as string[] })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("careProfileDialog.precautions")}
                placeholder={t("careProfileDialog.typeAndEnter")}
                inputProps={{
                  ...params.inputProps,
                  "data-testid": "profile-precautions",
                }}
              />
            )}
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              {t("careProfileDialog.careTeam")}
            </Typography>
          </Divider>
          <Stack spacing={1.5}>
            {draft.careTeam.map((m, i) => (
              <Stack direction="row" spacing={1} key={i} alignItems="center">
                <TextField
                  size="small"
                  label={t("careProfileDialog.role")}
                  value={m.role}
                  onChange={(e) => setTeamMember(i, { role: e.target.value })}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  label={t("careProfileDialog.name")}
                  value={m.name}
                  onChange={(e) => setTeamMember(i, { name: e.target.value })}
                  fullWidth
                />
                <IconButton
                  aria-label={t("careProfileDialog.removeMember")}
                  onClick={() => removeTeamMember(i)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addTeamMember}
              sx={{ alignSelf: "flex-start" }}
              data-testid="profile-add-team"
            >
              {t("careProfileDialog.addMember")}
            </Button>
          </Stack>

          <TextField
            size="small"
            label={t("careProfileDialog.notes")}
            placeholder={t("careProfileDialog.notesPlaceholder")}
            value={draft.notes ?? ""}
            onChange={(e) => patch({ notes: e.target.value || null })}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ "data-testid": "profile-notes" }}
          />

          <Typography variant="caption" color="text.secondary">
            <Trans
              t={t}
              i18nKey="careProfileDialog.footer"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
      )}
    </EditDrawer>
  );
}
